import { useCallback } from 'react'

import { UrlUpdateType, useQueryParams } from 'use-query-params'

/**
* Custom hook to manage filtering in a component using query parameters.
* @param {object} params - The query parameters to manage.
* @param {object} options - Options for the hook.
* @param {boolean} options.resetPageOnChange - Resets the page to 1 when filters change.
* @param {string} options.pageKey - The name of the page parameter.
* @param {UrlUpdateType} options.updateType - The type of URL update to perform.
* @returns {[object, function]} - An array containing the current query parameters and a function to update them.
* @example
*  const [{ includeDeleted, search }, updateFilters] = useFiltering({
    includeDeleted: withDefault(BooleanParam, false),
    search: withDefault(StringParam, ''),
  })
* const handleSearch = (val) => updateFilters({ search: val})
*/
export default function useFiltering(
	params,
	{ resetPageOnChange = false, pageKey = 'page', updateType = 'replaceIn' as UrlUpdateType } = {}
): any {
	const [queryParams, updateQueryParams] = useQueryParams(params)

	const handleUpdate = useCallback(
		(changes, updateTypeOverride: UrlUpdateType = updateType) =>
			updateQueryParams(
				resetPageOnChange ? { [pageKey]: 1, ...changes } : changes,
				updateTypeOverride
			),
		[pageKey, resetPageOnChange, updateQueryParams, updateType]
	)

	return [queryParams, handleUpdate]
}
